import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAdmin } from "@/context/AdminContext";
import { api, errText } from "@/lib/api";
import { MaskedLines } from "@/components/site/MaskedLines";
import { EditableText } from "@/components/cms/EditableText";
import { MediaUpload } from "@/components/cms/MediaUpload";
import { Lightbox } from "@/components/cms/Gallery";
import { BlockArea } from "@/components/cms/BlockArea";
import { FolderTabs } from "@/components/gallery/FolderTabs";
import { PhotoMasonry } from "@/components/gallery/PhotoMasonry";
import { VideoReel } from "@/components/gallery/VideoReel";

const TITLE_CLS = "font-display text-6xl sm:text-8xl lg:text-[9.5rem] font-extrabold leading-[0.86] tracking-[-0.05em]";
const FOLDERS = { photos: { label: "Photos", kind: "image", one: "photo", many: "photos" }, videos: { label: "Videos", kind: "video", one: "video", many: "videos" } };

const useGallery = () => {
  const [items, setItems] = useState([]);
  const reload = useCallback(() => {
    api.get("/gallery").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { photos: items.filter((x) => x.kind === "image"), videos: items.filter((x) => x.kind === "video"), reload };
};

const Kicker = ({ k, text, testId }) => (
  <p className="font-mono-label text-xs uppercase tracking-[0.25em] text-[#57534E]">
    <span className="mr-3 inline-block h-2 w-2 rounded-full bg-[#E62314] align-middle" />
    <EditableText k={k} text={text} testId={testId} />
  </p>
);

const GalleryHome = () => {
  const { photos, videos } = useGallery();
  const navigate = useNavigate();
  return (
    <div data-testid="gallery-page" className="pb-24">
      <section className="mx-auto max-w-7xl px-5 pt-10 sm:px-8 sm:pt-16 lg:px-12">
        <Kicker k="gallery_kicker" text="Photos & Videos" testId="gallery-kicker" />
        <EditableText k="gallery_title" as="h1" text="Gallery" className={`mt-6 ${TITLE_CLS}`} testId="gallery-title-edit">
          <MaskedLines testId="gallery-title" lines={[<>Gallery<span className="text-[#E62314]">.</span></>]} className={`mt-6 ${TITLE_CLS}`} />
        </EditableText>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }} className="mt-14">
          <FolderTabs onOpen={(k) => navigate(`/gallery/${k}`)} folders={[
            { key: "photos", ...FOLDERS.photos, items: photos },
            { key: "videos", ...FOLDERS.videos, items: videos },
          ]} />
        </motion.div>
      </section>
      <BlockArea slug="gallery" className="mt-16" />
    </div>
  );
};

const Folder = ({ folder }) => {
  const { editing } = useAdmin();
  const { photos, videos, reload } = useGallery();
  const [open, setOpen] = useState(-1);
  const f = FOLDERS[folder];
  const list = folder === "photos" ? photos : videos;

  const added = async (uploads) => {
    for (const m of uploads) {
      try { await api.post("/gallery", { media_id: m.id }); } catch (e) { toast.error(errText(e)); }
    }
    reload();
    toast.success(`${uploads.length} added to ${f.label}`);
  };

  return (
    <div data-testid={`gallery-folder-page-${folder}`} className="pb-24">
      <section className="mx-auto max-w-7xl px-5 pt-10 sm:px-8 sm:pt-16 lg:px-12">
        <Link to="/gallery" data-testid="gallery-back-link" className="group inline-flex items-center gap-2 text-sm font-bold transition-colors hover:text-[#E62314]">
          <span className="transition-transform duration-300 group-hover:-translate-x-1">←</span> Gallery
        </Link>
        <div className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <MaskedLines testId="gallery-folder-title" lines={[<>{f.label}<span className="text-[#E62314]">.</span></>]} className={TITLE_CLS} />
          <p className="font-mono-label text-sm text-[#57534E]" data-testid="gallery-folder-count">{String(list.length).padStart(2, "0")} {list.length === 1 ? f.one : f.many}</p>
        </div>
        {editing && (
          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="gallery-admin-tools">
            <MediaUpload multiple accept={f.kind === "image" ? "image/*" : "video/*"} onUploaded={added} label={`Add ${f.many}`} testId={`gallery-add-${folder}`} />
            <span className="text-xs text-[#57534E]">Hover an item to move it, give it a title or remove it.</span>
          </div>
        )}
        <div className="mt-10">
          {!list.length ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center border border-dashed border-black/20 px-6 text-center" data-testid={`gallery-empty-${folder}`}>
              <p className="font-display text-3xl font-extrabold tracking-tight">{f.label} <em className="font-serif-accent font-normal italic text-[#E62314]">coming soon</em></p>
              {editing && <p className="mt-2 text-sm text-[#57534E]">Use “Add {f.many}” above to fill this folder.</p>}
            </div>
          ) : folder === "photos" ? <PhotoMasonry photos={photos} editing={editing} reload={reload} onOpen={setOpen} />
            : <VideoReel videos={videos} editing={editing} reload={reload} onOpen={setOpen} />}
        </div>
      </section>
      {open >= 0 && list[open] && <Lightbox items={list.map((x) => ({ id: x.media_id, kind: x.kind }))} index={open} setIndex={setOpen} onClose={() => setOpen(-1)} />}
    </div>
  );
};

export default function GalleryPage() {
  const { folder } = useParams();
  if (!folder) return <GalleryHome />;
  if (!FOLDERS[folder]) return <NotFound />;
  return <Folder key={folder} folder={folder} />;
}
